import React, { useEffect, useState, useContext } from "react";
import "./HomePage.css";
import { LandingPageContext } from "../../contexts/LandingPageContext";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import SplashScreen from "../SplashScreen";
import FirstSection from "../FirstSection/FirstSection";
import Header from "../Header/Header";
import Hero from "../Hero/Hero";
import ArtSection from "../ArtSection/ArtSection";
import NowSection from "../NowSection/NowSection";
import ContactSection from "../ContactSection/ContactSection";
import NewSection from "../NewSection/NewSection";
import LazyImage from "../LazyImage";

const HomePage = () => {
    const [introShown, setIntroShown] = useState(false);
    const { globalColors, scrollTo, getScrollSmoother, gallerySection } = useContext(LandingPageContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        // SEO: set the page title and meta description for the home page
        const title = 'Tafelberg — Historic Venue & Gallery in Nuremberg';
        const description = "Tafelberg — a historic, intimate venue in Nuremberg's Weißgerbergasse for exhibitions, events and private gatherings. Host up to 40 guests in a unique, art-filled atmosphere.";

        try {
            document.title = title;
            let metaDesc = document.querySelector('meta[name="description"]');
            if (!metaDesc) {
                metaDesc = document.createElement('meta');
                metaDesc.setAttribute('name', 'description');
                document.head.appendChild(metaDesc);
            }
            metaDesc.setAttribute('content', description);

            // Open Graph tags
            const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta');
            ogTitle.setAttribute('property', 'og:title');
            ogTitle.setAttribute('content', title);
            if (!ogTitle.parentNode) document.head.appendChild(ogTitle);

            const ogDesc = document.querySelector('meta[property="og:description"]') || document.createElement('meta');
            ogDesc.setAttribute('property', 'og:description');
            ogDesc.setAttribute('content', description);
            if (!ogDesc.parentNode) document.head.appendChild(ogDesc);

            const ogUrl = document.querySelector('meta[property="og:url"]') || document.createElement('meta');
            ogUrl.setAttribute('property', 'og:url');
            ogUrl.setAttribute('content', 'https://www.tafelberg-nuernberg.de/');
            if (!ogUrl.parentNode) document.head.appendChild(ogUrl);

            const ogImage = document.querySelector('meta[property="og:image"]') || document.createElement('meta');
            ogImage.setAttribute('property', 'og:image');
            ogImage.setAttribute('content', 'https://res.cloudinary.com/dqssg8tws/image/upload/v1769899188/hero_wfidig.png');
            if (!ogImage.parentNode) document.head.appendChild(ogImage);

            const twitterCard = document.querySelector('meta[name="twitter:card"]') || document.createElement('meta');
            twitterCard.setAttribute('name', 'twitter:card');
            twitterCard.setAttribute('content', 'summary_large_image');
            if (!twitterCard.parentNode) document.head.appendChild(twitterCard);
        } catch (err) {
            // best-effort only
            console.warn('Failed to update meta tags', err);
        }

    }, []);

    useEffect(() => {
        if (!globalColors) return;

        const colors = {
            titleColor: "#ffffff",
            textColor: "#ffffff",
            sectionTitleColor: "#ffffff",
            bodyTextColor: "#ffffff",
            artSectionTextColor: "#222222",
            artSectionRemainingTextColor: "#777777",
            heroTextColor: "#ffffff",
            contactTextColor: "#ffffff",
            ...globalColors,
        };

        document.documentElement.style.setProperty("--global-title-color", colors.titleColor);
        document.documentElement.style.setProperty("--global-text-color", colors.textColor);
        document.documentElement.style.setProperty("--global-section-title-color", colors.sectionTitleColor);
        document.documentElement.style.setProperty("--global-body-text-color", colors.bodyTextColor);
        document.documentElement.style.setProperty("--global-art-section-text-color", colors.artSectionTextColor);
        document.documentElement.style.setProperty("--global-art-section-remaining-text-color", colors.artSectionRemainingTextColor);
        document.documentElement.style.setProperty("--global-hero-text-color", colors.heroTextColor);
        document.documentElement.style.setProperty("--global-contact-text-color", colors.contactTextColor);
    }, [globalColors]);

    // compute and expose header offset for CSS use (so section titles can use scroll-margin-top)
    useEffect(() => {
        const computeAndSetHeaderOffset = () => {
            try {
                const headerEl = document.querySelector('.header');
                if (!headerEl) {
                    document.documentElement.style.setProperty('--header-offset', '28px');
                    return;
                }
                const rect = headerEl.getBoundingClientRect();
                const headerTop = Math.max(0, Math.round(rect.top));
                const headerHeight = Math.round(rect.height || 0);
                const offset = headerTop + headerHeight + 8;
                document.documentElement.style.setProperty('--header-offset', `${offset}px`);
            } catch (err) {
                document.documentElement.style.setProperty('--header-offset', '28px');
            }
        };

        computeAndSetHeaderOffset();
        window.addEventListener('resize', computeAndSetHeaderOffset);
        // also update on orientation change
        window.addEventListener('orientationchange', computeAndSetHeaderOffset);

        return () => {
            window.removeEventListener('resize', computeAndSetHeaderOffset);
            window.removeEventListener('orientationchange', computeAndSetHeaderOffset);
        };
    }, []);

    const computeOffset = () => {
        try {
            const headerEl = document.querySelector('.header');
            if (!headerEl) return 28;
            const rect = headerEl.getBoundingClientRect();
            const headerTop = Math.max(0, Math.round(rect.top));
            const headerHeight = Math.round(rect.height || 0);
            return headerTop + headerHeight + 8;
        } catch (e) {
            return 28;
        }
    };

    // If navigation passed a target anchor (e.g. from NavMenu when coming from /gallery), wait for ScrollSmoother and DOM then scroll
    useEffect(() => {
        const target = (location && location.state && location.state.targetAnchor) ? location.state.targetAnchor : (location && location.hash ? location.hash : null);
        if (!target) return;

        let attempts = 0;
        const maxAttempts = 120; // ~12s

        const interval = setInterval(() => {
            const smoother = getScrollSmoother && getScrollSmoother();
            const sectionEl = (typeof document !== 'undefined') ? document.querySelector(target) : null;

            if ((smoother || true) && sectionEl) {
                // small delay to allow layout to settle
                setTimeout(() => {
                    try {
                        if (typeof scrollTo === 'function') {
                            const offset = computeOffset();
                            scrollTo(target, { offset });
                        }
                    } catch (e) { /* ignore */ }

                    try {
                        // clear navigation state so back/refresh behaves normally
                        navigate(location.pathname, { replace: true, state: {} });
                    } catch (e) { /* ignore */ }
                }, 80);

                clearInterval(interval);
            } else if (++attempts >= maxAttempts) {
                clearInterval(interval);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [location, getScrollSmoother, scrollTo, navigate]);

    // Global anchor handling: intercept <a href="#..."> clicks and hashchange so all anchors land with the section title below the header
    useEffect(() => {
        const onDocClick = (e) => {
            try {
                let el = e.target;
                while (el && el.tagName !== 'A') el = el.parentElement;
                if (!el) return;
                const href = el.getAttribute('href');
                if (!href || !href.startsWith('#')) return;

                // Only handle same-page style anchors or navigate to home if not on home
                e.preventDefault();
                const target = href;

                if (location.pathname !== '/') {
                    try {
                        if (typeof window !== 'undefined' && window.sessionStorage) sessionStorage.setItem('skipSplash', '1');
                    } catch (err) { /* ignore */ }
                    try {
                        navigate(`/${target}`, { state: { targetAnchor: target } });
                    } catch (err) {
                        navigate('/', { state: { targetAnchor: target } });
                    }
                } else {
                    // same-page: use context scrollTo (which has header-aware default)
                    try {
                        scrollTo(target);
                    } catch (err) { /* ignore */ }
                }
            } catch (err) {
                // swallow any errors from this global handler
            }
        };

        const onHashChange = () => {
            try {
                const target = window.location.hash || null;
                if (!target) return;
                // use header-aware offset
                const offset = computeOffset();
                try { scrollTo(target, { offset }); } catch (e) { /* ignore */ }
            } catch (err) { /* ignore */ }
        };

        document.addEventListener('click', onDocClick);
        window.addEventListener('hashchange', onHashChange);

        return () => {
            document.removeEventListener('click', onDocClick);
            window.removeEventListener('hashchange', onHashChange);
        };
    }, [location, navigate, scrollTo]);


    return (
        <div className="homepage">
            {!introShown && <SplashScreen onComplete={() => setIntroShown(true)} />}
            <Header />

            <h1 className="visually-hidden">{t("hero.title")}</h1>

            <Hero />
            <FirstSection />

            <div className="circle">
                <div className="circle-two">
                    <ArtSection />
                    <NowSection />
                </div>
            </div>

            <div
                className="gallery-teaser"
                role="link"
                tabIndex={0}
                onClick={() => navigate('/gallery')}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate('/gallery');
                    }
                }}
            >
                <div className="gallery-teaser-inner">
                    <div className="gallery-teaser-images">
                        {(Array.isArray(gallerySection?.images) && gallerySection.images.length > 0
                            ? gallerySection.images.slice(0, 3)
                            : ['/gallery/1.jpg', '/gallery/2.jpg', '/gallery/3.jpg']
                        ).map((src, i) => (
                            <LazyImage key={`${i}-${src}`} src={src} alt={`Gallery preview ${i + 1}`} />
                        ))}
                    </div>
                    <div className="gallery-teaser-cta">
                        <div className="gallery-teaser-title">
                            {t("gallerySection.headerTitle")}
                        </div>
                        <div className="gallery-teaser-sub">
                            {t("gallerySection.headerSubtitle")}
                        </div>
                    </div>
                </div>
            </div>

            <NewSection />

            <ContactSection className="contact-section" />
        </div>
    );
};

export default HomePage;
